import { useMemo, useEffect, type FC, useContext } from 'react';
import cn from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Waypoint } from 'react-waypoint';
import ModerationAlert from '@wix/da-ds/pkg/Icons/16x16/ModerationAlert';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { STUDIO_VIDEO_GENERATIONS_STREAM } from '../../../../constants';
import VideoCreatorPlaceholder from './VideoCreatorPlaceholder';
import VideoFile from './VideoFile';
import {
  getVideoGenerationsByIds,
  getVideoCreatorPendingGenerations,
} from '../../../selectors/studio';
import VideoPendingGeneration from './VideoPendingGeneration';
import { getModelPropertiesById } from '../ModelProperties';
import { VideoCreatorPendingGeneration } from '../../../types/store';
import {
  addVideoCreatorPendingGeneration,
  removeVideoCreatorPendingGeneration,
} from '../../../actions/studio/videoCreator';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './VideoCreatorFiles.scss';

export interface Props {}

const VideoCreatorFiles: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const pendingGenerations = useSelector(getVideoCreatorPendingGenerations);

  // Get video generation IDs from stream
  // Note: Stream is initialized by the saga in handleInitializeVideoCreator
  const {
    items: generationIds = [],
    hasMore,
    fetchNext,
    isFetching,
  } = useStream<number>(STUDIO_VIDEO_GENERATIONS_STREAM.streamId);

  const videoGenerations = useSelector(state =>
    getVideoGenerationsByIds(state, generationIds || [])
  );

  useEffect(() => {
    const existingIds = new Set(pendingGenerations.map(p => p.generationId));
    const requestedVideos = videoGenerations.filter(
      video =>
        video.status === 'requested' && !existingIds.has(video.generationId)
    );

    requestedVideos.forEach(video => {
      const modelProps = getModelPropertiesById(video.modelId);
      if (!modelProps) {
        return;
      }
      const pendingGeneration: VideoCreatorPendingGeneration = {
        generationId: video.generationId,
        modelType: modelProps.type,
        prompt: video.prompt,
        aspectRatio: video.aspectRatio,
        duration: video.duration,
        referenceDeviation: video.referenceDeviation,
        sound: video.sound ?? false,
      };
      dispatch(addVideoCreatorPendingGeneration(pendingGeneration));
    });
  }, [videoGenerations, pendingGenerations, dispatch]);

  // This is a failsafe. If we fetch a new batch of videos,
  // and one of them is completed and has the generationId of a pending generation, remove
  // the pending generation. I think there is a small window when this could be needed
  // if the video completes right when we are making the video_init call.
  useEffect(() => {
    const pendingIds = new Set(pendingGenerations.map(p => p.generationId));
    const completedVideos = videoGenerations.filter(
      video =>
        video.status !== 'requested' && pendingIds.has(video.generationId)
    );

    completedVideos.forEach(video => {
      dispatch(removeVideoCreatorPendingGeneration(video.generationId));
    });
  }, [videoGenerations, pendingGenerations, dispatch]);

  const showPlaceholder = useMemo(() => {
    const hasCompletedVideos = videoGenerations.some(
      video => video.status !== 'requested'
    );
    return pendingGenerations.length === 0 && !hasCompletedVideos;
  }, [pendingGenerations, videoGenerations]);

  return (
    <div className={cn(s['root'], isMobile && s['mobile'])}>
      <div className={s['header']}>
        <div className={s['header-title']}>
          {t('studio.videoCreator.resultsLabel')}
        </div>
        <div className={s['header-detail']}>
          <ModerationAlert className={s['alert-icon']} size={IconSize.SMALL} />
          <div className={s['alert-text']}>
            {t('studio.videoCreator.resultsDisclaimer')}
          </div>
        </div>
      </div>

      <div className={s['scroller']}>
        {showPlaceholder && <VideoCreatorPlaceholder />}
        {pendingGenerations.length > 0 && (
          <>
            {pendingGenerations.map(pendingGeneration => (
              <VideoPendingGeneration
                key={pendingGeneration.generationId}
                pendingGeneration={pendingGeneration}
              />
            ))}
          </>
        )}
        {videoGenerations.length > 0 && (
          <>
            {videoGenerations.map(video =>
              video.status !== 'requested' ? (
                <VideoFile key={video.generationId} videoGeneration={video} />
              ) : null
            )}
            {hasMore && (
              <Waypoint
                onEnter={() => {
                  if (!isFetching) {
                    fetchNext();
                  }
                }}
                bottomOffset="20%"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

VideoCreatorFiles.displayName = 'VideoCreatorFiles';
export default VideoCreatorFiles;
